import React, { useEffect, useState, useCallback } from "react";
import { observer } from "mobx-react-lite";
import {
  CContainer,
  CCard,
  CCardBody,
  CFormCheck,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CBadge,
} from "@coreui/react";
import { CChartBar } from "@coreui/react-chartjs";
import settingsStore from "../../store/SettingsStore";
import periodPrefsStore from "../../store/PeriodPrefsStore";
import PeriodFilters from "../UI/PeriodFilters/PeriodFilters";
import ErrorAlert from "../UI/Status/ErrorAlert";
import { SkeletonChart } from "../UI/Skeleton/Skeleton";
import { compareServers } from "../../api/reportsApi";
import { fmtInt } from "../../utils/chartHelpers";
import { logReportView } from "../../api/reportLogApi";
import { formatYmd } from "../../utils/dateRange";

const PALETTE = ["#41B883", "#E46651", "#00D8FF", "#DD1B16", "#f6b93b", "#32aebe"];

const CompareServersPage = observer(() => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [fromDate, setFromDate] = useState(periodPrefsStore.fromDate);
  const [toDate, setToDate] = useState(periodPrefsStore.toDate);
  const [quickRange, setQuickRange] = useState(periodPrefsStore.quickRange);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(
    async (from = fromDate, to = toDate, ids = selectedIds) => {
      if (ids.length === 0 || !from || !to) return;
      setLoading(true);
      setError(null);
      try {
        const data = await compareServers(ids.map(Number), from, to);
        setResults(data.results || []);
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    },
    [fromDate, toDate, selectedIds],
  );

  useEffect(() => {
    const init = async () => {
      const userId = localStorage.getItem("userId");
      if (userId) await settingsStore.getServers(userId);
      if (!fromDate || !toDate) {
        const today = new Date();
        const past = new Date(today);
        past.setDate(today.getDate() - 7);
        setFromDate(formatYmd(past));
        setToDate(formatYmd(today));
      }
      logReportView({ event: "report_view", reportKey: "compare_servers", reportTitle: "Сравнение точек", userId, path: "/compare-servers" });
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (settingsStore.servers?.length && selectedIds.length === 0) {
      setSelectedIds(settingsStore.servers.slice(0, 2).map((s) => String(s.id)));
    }
  }, [settingsStore.servers, selectedIds.length]);

  const toggleServer = (id) => {
    const key = String(id);
    setSelectedIds((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]));
  };

  const handleRefresh = (from, to) => {
    periodPrefsStore.setPeriod(from, to, quickRange);
    load(from, to, selectedIds);
  };

  return (
    <CContainer style={{ flex: "1", marginTop: "120px" }}>
      <h4 className="mb-3">Сравнение точек</h4>
      <p className="text-body-secondary small">
        Бенчмарк нескольких подключённых серверов за один период: выручка, средний чек, гости, фудкост рядом.
      </p>

      <div className="d-flex flex-wrap gap-3 mb-3">
        {(settingsStore.servers || []).map((s) => (
          <CFormCheck
            key={s.id}
            id={`srv-${s.id}`}
            label={s.name}
            checked={selectedIds.includes(String(s.id))}
            onChange={() => toggleServer(s.id)}
          />
        ))}
      </div>

      <PeriodFilters
        fromDate={fromDate}
        toDate={toDate}
        onFromChange={setFromDate}
        onToChange={setToDate}
        onRefresh={handleRefresh}
        quickRange={quickRange}
        onQuickRangeChange={setQuickRange}
        disabled={loading}
        loading={loading}
        refreshLabel="Сравнить"
      />
      <ErrorAlert error={error} />

      <CCard className="mt-3">
        <CCardBody>
          {loading && results.length === 0 && <SkeletonChart />}
          {!loading && results.length > 0 && (
            <>
              <div style={{ maxHeight: 220 }} className="mb-3">
                <CChartBar
                  data={{
                    labels: results.map((r) => r.name),
                    datasets: [
                      {
                        label: "Выручка, ₽",
                        backgroundColor: results.map((_, i) => PALETTE[i % PALETTE.length]),
                        data: results.map((r) => (r.ok ? r.revenue : 0)),
                      },
                    ],
                  }}
                  options={{ maintainAspectRatio: false, plugins: { legend: { display: false } } }}
                />
              </div>
              <CTable small hover responsive className="mb-0">
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell>Сервер</CTableHeaderCell>
                    <CTableHeaderCell>Выручка</CTableHeaderCell>
                    <CTableHeaderCell>Гости</CTableHeaderCell>
                    <CTableHeaderCell>Чеки</CTableHeaderCell>
                    <CTableHeaderCell>Ср. чек</CTableHeaderCell>
                    <CTableHeaderCell>Фудкост</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {results.map((r) => (
                    <CTableRow key={r.server_id}>
                      <CTableDataCell>{r.name}</CTableDataCell>
                      {r.ok ? (
                        <>
                          <CTableDataCell>{fmtInt(r.revenue)} ₽</CTableDataCell>
                          <CTableDataCell>{fmtInt(r.guests)}</CTableDataCell>
                          <CTableDataCell>{fmtInt(r.orders)}</CTableDataCell>
                          <CTableDataCell>{r.average_check != null ? `${fmtInt(r.average_check)} ₽` : "—"}</CTableDataCell>
                          <CTableDataCell>{r.avg_cost_percent != null ? `${r.avg_cost_percent}%` : "—"}</CTableDataCell>
                        </>
                      ) : (
                        <CTableDataCell colSpan={5}>
                          <CBadge color="danger">{r.error || "Ошибка"}</CBadge>
                        </CTableDataCell>
                      )}
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
            </>
          )}
          {!loading && results.length === 0 && !error && (
            <div className="text-body-secondary small">Выберите серверы и период, затем нажмите «Сравнить».</div>
          )}
        </CCardBody>
      </CCard>
    </CContainer>
  );
});

export default CompareServersPage;
